// Production stand-in for the design prototype's <image-slot> custom element.
// The prototype slot was a design-time drag-to-fill placeholder; here it
// renders the supplied image, or the same styled empty state until real
// imagery exists for the slot.
import { useState, type CSSProperties } from 'react';

export interface ImageSlotProps {
  shape?: 'rect' | 'rounded' | 'circle' | 'pill';
  radius?: number;
  fit?: 'cover' | 'contain' | 'fill';
  position?: string;
  placeholder?: string;
  src?: string;
  alt?: string;
  className?: string;
  style?: CSSProperties;
}

function borderRadius(shape: ImageSlotProps['shape'], radius: number): string | number {
  if (shape === 'circle') return '50%';
  if (shape === 'pill') return 999;
  if (shape === 'rect') return 0;
  return radius;
}

export function ImageSlot({
  shape = 'rounded',
  radius = 12,
  fit = 'cover',
  position = '50% 50%',
  placeholder = 'Image',
  src,
  alt = '',
  className,
  style,
}: ImageSlotProps) {
  // Tracks the specific src that failed, not just a boolean - so a later
  // prop change (e.g. a fresh upload replacing a broken cover) clears the
  // fallback on its own instead of a stale ImageSlot instance (no `key`
  // change; CoverUploader/AssetRow etc. just pass a new `src`) being stuck
  // showing the placeholder for a URL it's no longer even rendering.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const failed = src !== undefined && failedSrc === src;
  const frame: CSSProperties = {
    overflow: 'hidden',
    borderRadius: borderRadius(shape, radius),
    background: 'var(--ss-panel-strong)',
    border: '1px solid var(--ss-border-soft)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...style,
  };
  if (src && !failed) {
    return (
      <div className={className} style={frame}>
        <img
          src={src}
          alt={alt}
          onError={() => setFailedSrc(src)}
          style={{ width: '100%', height: '100%', objectFit: fit, objectPosition: position, display: 'block' }}
        />
      </div>
    );
  }
  // No image yet (or the real one failed to load - a deleted asset, an
  // expired signed URL, a network hiccup): render the same plain, neutral
  // empty state rather than the browser's own broken-image icon. Just the
  // muted panel, no instructional text and no icon. The `placeholder` string
  // is kept only as the accessible label (invisible), never shown on screen.
  return <div className={className} style={frame} role="img" aria-label={placeholder} />;
}
